import { debounce } from '@mui/material';
import { PartnerOnesheet } from '@provision/entity-types';
import { useMemo, useState } from 'react';
import { SidePanelTabs } from 'src/components';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Address, useGroupRules, UserGroupRules } from 'src/graphql';
import { usePartnerOnesheet } from 'src/graphql/entities/partner-onesheet';
import { NullableDeepPartial } from 'src/graphql/types';
import { MembershipTab } from './membership-tab';
import { NotesTab } from './notes-tab';
import { PartnerTabContactPartner } from './partner-contact-info';
import { RecentAnnouncementsTab } from './recent-announcements-tab';
import { SupportNotesTab } from './support-notes-tab';
import { SystemInfoTab } from './system-info-tab';

interface PartnerTabsProps {
  partner: PartnerTabContactPartner;
  address?: Partial<Address>;
}

enum PartnerTabs {
  Notes = 'notes',
  Membership = 'membership',
  RecentAnnouncements = 'recent-announcements',
  SystemInfo = 'system-info',
  SupportNotes = 'support-notes',
}

interface TabContentProps {
  partner: PartnerTabContactPartner;
  address?: Partial<Address>;
  activeTab: PartnerTabs;
}

const TabContent = ({ activeTab, partner, address }: TabContentProps) => {
  const partnerDetails = partner?.partnerOnesheet?.[0];
  const { createPartnerOneSheet, updatePartnerOnesheet } = usePartnerOnesheet(
    partner?.partnerId,
  );
  const { groupRules } = useGroupRules();
  const canEditPartnerTab = groupRules[UserGroupRules.AccessToEditPartnerTab];

  const handleFieldChange = useMemo(
    () =>
      debounce(async (field: string, value: string) => {
        const patch = { [field]: value };
        const partnerOneSheet: NullableDeepPartial<PartnerOnesheet> = {
          ...partnerDetails,
          partnerId: partner.partnerId,
          ...patch,
        };

        if (partnerDetails) {
          await updatePartnerOnesheet(patch, partnerOneSheet);
        } else {
          await createPartnerOneSheet(patch, partnerOneSheet);
        }
      }, SEARCH_DEBOUNCE_DELAY),
    [createPartnerOneSheet, partner.partnerId, partnerDetails, updatePartnerOnesheet],
  );

  if (activeTab === PartnerTabs.Notes) {
    return (
      <NotesTab
        partner={partner}
        onFieldChange={handleFieldChange}
        readonly={!canEditPartnerTab}
      />
    );
  }

  if (activeTab === PartnerTabs.Membership) {
    return <MembershipTab partner={partner} address={address} key={address?.brandId} />;
  }

  if (activeTab === PartnerTabs.RecentAnnouncements) {
    return (
      <RecentAnnouncementsTab
        partner={partner}
        onFieldChange={handleFieldChange}
        readonly={!canEditPartnerTab}
      />
    );
  }

  if (activeTab === PartnerTabs.SystemInfo) {
    return (
      <SystemInfoTab
        partner={partner}
        onFieldChange={handleFieldChange}
        readonly={!canEditPartnerTab}
      />
    );
  }

  if (activeTab === PartnerTabs.SupportNotes) {
    return (
      <SupportNotesTab
        partner={partner}
        onFieldChange={handleFieldChange}
        readonly={!canEditPartnerTab}
      />
    );
  }

  return null;
};

const tabs = [
  {
    value: PartnerTabs.Notes,
    label: 'Notes',
    icon: 'note',
  },
  {
    value: PartnerTabs.Membership,
    label: 'Membership',
    icon: 'card_membership',
  },
  {
    value: PartnerTabs.RecentAnnouncements,
    label: 'Recent Announcements',
    icon: 'campaign',
  },
  {
    value: PartnerTabs.SystemInfo,
    label: 'System Info',
    icon: 'google_home_devices',
  },
  {
    value: PartnerTabs.SupportNotes,
    label: 'Support Notes',
    icon: 'help_clinic',
  },
];

export const PartnerInfoTabs = ({ partner, address }: PartnerTabsProps) => {
  const [activeTab, setActiveTab] = useState(PartnerTabs.Notes);

  return (
    <SidePanelTabs
      tabSx={{ flexGrow: 1 }}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabs={tabs}
    >
      <TabContent activeTab={activeTab} partner={partner} address={address} />
    </SidePanelTabs>
  );
};
